import React, {useEffect, useState} from 'react';

import {DiscoveryUnit} from '@udemy/discovery-api';
import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {useI18n} from '@udemy/i18n';
import {CourseUnit} from '@udemy/react-discovery-units';

import {
    BrowseCourseSearchQueryVariables,
    CourseSearchTriggerType,
    useBrowseCourseSearchQuery,
} from 'src/generated/graphql';
import {createSearchDataFromGraphQLResponse} from 'src/lib/gql-mappers';

const FALLBACK_FETCH_PAGE_SIZE = 20;
const FALLBACK_CAROUSEL_MAX_COURSES = 10;

interface VideoCoursesFallbackCarouselProps {
    topicId: number;
    pageType: string;
}

export const VideoCoursesFallbackCarousel = ({
    topicId,
    pageType,
}: VideoCoursesFallbackCarouselProps) => {
    const {gettext} = useI18n();
    const [unit, setUnit] = useState<DiscoveryUnit | null>(null);

    useEffect(() => {
        let isMounted = true;

        const fetchFallbackCourses = async () => {
            const variables: BrowseCourseSearchQueryVariables = {
                page: 0,
                pageSize: FALLBACK_FETCH_PAGE_SIZE,
                filters: {pageId: topicId.toString()},
                context: {triggerType: CourseSearchTriggerType.BrowseTopic},
            };

            try {
                const response = await useBrowseCourseSearchQuery.fetcher(variables)();
                const nonPracticeTestResults = (response?.courseSearch?.results ?? []).filter(
                    (result) => result && !result.course.isPracticeTestCourse,
                );
                const filteredResponse = response?.courseSearch
                    ? {
                          ...response,
                          courseSearch: {...response.courseSearch, results: nonPracticeTestResults},
                      }
                    : response;
                const searchData = createSearchDataFromGraphQLResponse(undefined, filteredResponse);
                const items = (searchData?.items ?? []).slice(0, FALLBACK_CAROUSEL_MAX_COURSES);

                if (isMounted && items.length > 0) {
                    setUnit({
                        type: 'featured_course',
                        item_type: 'course',
                        title: gettext('Video Courses'),
                        items,
                        remaining_item_count: 0,
                    } as DiscoveryUnit);
                }
            } catch {
                // Best-effort fallback: on failure, simply render nothing.
            }
        };

        fetchFallbackCourses();

        return () => {
            isMounted = false;
        };
    }, [topicId, gettext]);

    if (!unit) {
        return null;
    }

    return (
        <div className="component-margin" data-purpose="video-courses-fallback-carousel">
            <FunnelLogContextProvider pageType="topic">
                <CourseUnit
                    unit={unit}
                    discoveryUnitsStore={{pageType, units: [unit]}}
                    layout="singlerow"
                    showTitle={true}
                    showPager={true}
                    gridCols={4}
                />
            </FunnelLogContextProvider>
        </div>
    );
};
